import * as React from 'react'; // https://github.com/aelbore/esbuild-jest/issues/61
import Serializer, {
  type SerializeArguments,
  type DeserializeArguments,
} from './Serializer';
import { Editor, Element } from 'slate';
import { type TableElement } from '../../types/aha-editor';
import {
  columnWidthsFromTable,
  hasTableHeaderColumn,
  hasTableHeaderRow,
  isTableWithoutCells,
  isTableWrapper,
} from '../helpers/tableHelpers';
import { isTableElement } from '../../types/guards';
import { isHTMLElementNode } from '../helpers/serializerHelpers';
import classnames from 'classnames';
import ColorUtils from '../../util/color';

interface TableElementDataAttributes {
  borderColor?: string;
  alternateRows?: boolean;
  alternateRowColor?: string;
  numberedRows?: boolean;
}

class SerializerTable extends Serializer {
  static serialize({ node, children }: SerializeArguments) {
    if (!isTableElement(node)) return;

    const style: React.CSSProperties = {};
    if (node.table_width) {
      style.width =
        typeof node.table_width === 'number'
          ? `${node.table_width}px`
          : node.table_width;
      style.maxWidth = '100%';
    }

    const columnWidths: (number | null)[] = node.table_columnWidths ?? [];

    // Calculate numbered row column width
    const bodyChildrenArray = React.Children.toArray(children);
    const numBodyRows = node.table_headerRow
      ? bodyChildrenArray.length - 1
      : bodyChildrenArray.length;
    const numDigits = numBodyRows.toString().length;
    const numberedRowColumnWidth = `${numDigits}ch + 16px`;

    const colgroup =
      columnWidths.length > 0 || node.table_numberedRows ? (
        <colgroup>
          {node.table_numberedRows && (
            <col style={{ width: `calc(${numberedRowColumnWidth})` }} />
          )}
          {columnWidths.map((width, i) => (
            <col
              style={{
                width: typeof width === 'number' ? `${width}%` : undefined,
              }}
              key={i}
            />
          ))}
        </colgroup>
      ) : null;

    // Add numbered cells to rows if enabled
    const allChildren = React.Children.toArray(children);
    const processedChildren = node.table_numberedRows
      ? allChildren.map((child, index) => {
          if (!React.isValidElement(child) || child.type !== 'tr') return child;

          const isHeaderRow = node.table_headerRow && index === 0;
          const rowNumber = node.table_headerRow ? index : index + 1;

          const numberedCell = isHeaderRow ? (
            <th
              key="row-number"
              className="Editor__th Editor__th--row-number-header"
              contentEditable={false}
              suppressContentEditableWarning
            />
          ) : (
            <th
              key="row-number"
              className="Editor__th Editor__th--row-number"
              contentEditable={false}
              suppressContentEditableWarning
              scope="row"
            >
              {rowNumber}
            </th>
          );

          return React.cloneElement(
            child,
            {},
            numberedCell,
            ...React.Children.toArray(child.props.children)
          );
        })
      : allChildren;

    // Split into thead and tbody
    let headChildren: React.ReactNode[] | null = null;
    let bodyChildren = processedChildren;
    let thead: React.ReactNode | null = null;

    if (node.table_headerRow && processedChildren.length > 0) {
      headChildren = processedChildren.slice(0, 1);
      bodyChildren = processedChildren.slice(1);
      thead = <thead className="Editor__thead">{headChildren[0]}</thead>;
    }

    const attributes: Record<string, string> = {};

    // Combine table styling attributes into a single JSON data attribute
    const tableElementData: TableElementDataAttributes = {};
    if (node.table_borderColor) {
      tableElementData.borderColor = node.table_borderColor;
    }
    if (node.table_alternateRows) {
      tableElementData.alternateRows = true;
      if (node.table_alternateRowColor) {
        tableElementData.alternateRowColor = node.table_alternateRowColor;
      }
    }
    if (node.table_numberedRows) {
      tableElementData.numberedRows = true;
    }

    if (Object.keys(tableElementData).length > 0) {
      attributes['data-table-element'] = JSON.stringify(tableElementData);
    }

    const table = (
      <table
        className={classnames('Editor__table', {
          'Editor__table--sticky-header': node.table_headerRow,
        })}
        {...attributes}
      >
        {colgroup}
        {thead}
        <tbody className="Editor__tbody">{bodyChildren}</tbody>
      </table>
    );

    if (style.width) {
      return (
        <div className="table-wrapper" style={style}>
          {table}
        </div>
      );
    }

    return table;
  }

  static deserialize({
    el,
    next,
    context,
  }: DeserializeArguments): TableElement | undefined {
    if (!isHTMLElementNode(el)) return;
    if (el.tagName.toLowerCase() !== 'table' && !isTableWrapper(el)) return;

    const { editor } = context;
    if (!editor) return;

    const table = isTableWrapper(el)
      ? (el.firstElementChild as HTMLTableElement)
      : (el as HTMLTableElement);
    if (!table?.firstElementChild) return;

    if (isTableWithoutCells(table)) {
      return;
    }

    const columnWidths = columnWidthsFromTable(table);

    const totalWidth = columnWidths.reduce((sum, width) => sum + width, 0);
    const normalizedWidths =
      totalWidth !== 0 &&
      columnWidths.map((width) => (width / totalWidth) * 100);

    const data: Omit<TableElement, 'type' | 'children'> = {};

    if (el.dataset.tableElement) {
      try {
        const parsed = JSON.parse(el.dataset.tableElement);

        // Ensure parsed value is a non-null object
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          const tableElement = parsed as TableElementDataAttributes;

          if (
            tableElement.borderColor &&
            ColorUtils.isValidHexColor(tableElement.borderColor)
          ) {
            data.table_borderColor = tableElement.borderColor;
          }

          if (tableElement.alternateRows) {
            data.table_alternateRows = true;

            if (
              tableElement.alternateRowColor &&
              ColorUtils.isValidHexColor(tableElement.alternateRowColor)
            ) {
              data.table_alternateRowColor = tableElement.alternateRowColor;
            }
          }

          if (tableElement.numberedRows) {
            data.table_numberedRows = true;
          }
        }
      } catch (e) {
        // Ignore if JSON parsing fails
      }
    }

    if (hasTableHeaderRow(table)) {
      data.table_headerRow = true;
    }

    if (hasTableHeaderColumn(table)) {
      data.table_headerColumn = true;
    }

    // COMPAT: Google Sheets specifies a width of 0px for some reason
    if (el.style.width && el.style.width !== '0px') {
      data.table_width = el.style.width;
    }

    if (normalizedWidths) {
      data.table_columnWidths = normalizedWidths;
    }

    // Tables should only have block children
    const children = next(table.children, {}).filter(
      (node) => Element.isElement(node) && Editor.isBlock(editor, node)
    );

    return {
      type: 'table',
      ...data,
      children,
    };
  }
}
export default SerializerTable;
